<template>
  <div :class="$style.feedbackSection" role="group" aria-label="Feedback">
    <div :class="$style.feedbackGroup">
      <span :class="$style.text">{{ t('neighbourhood.insights.feedback') }}</span>
      <button
        :class="[
          $style.button,
          $style.positiveButton,
          { [$style.selected]: selectedFeedback === 'positive' },
        ]"
        :aria-pressed="selectedFeedback === 'positive'"
        aria-label="Yes, this is helpful"
        @click="handleFeedback('positive')"
      >
        <Smile :class="$style.buttonIcon" aria-hidden="true" />
        <span :class="$style.buttonText">{{ t('neighbourhood.insights.helpful') }}</span>
      </button>
      <button
        :class="[
          $style.button,
          $style.negativeButton,
          { [$style.selected]: selectedFeedback === 'negative' },
        ]"
        :aria-pressed="selectedFeedback === 'negative'"
        aria-label="No, this is not helpful"
        @click="handleFeedback('negative')"
      >
        <Frown :class="$style.buttonIcon" aria-hidden="true" />
        <span :class="$style.buttonText">{{ t('neighbourhood.insights.notHelpful') }}</span>
      </button>
      <a
        v-if="showShareLink"
        :class="[$style.shareLink, { [$style.shareLinkDisabled]: !isShareEnabled }]"
        :href="shareLinkHref || undefined"
        :aria-disabled="!isShareEnabled"
        target="_blank"
        rel="noopener noreferrer"
        @click="handleShareClick"
      >
        <span>{{ t('neighbourhood.insights.shareFeedback') }}</span>
        <ExternalLink :class="$style.shareIcon" aria-hidden="true" />
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ExternalLink, Frown, Smile } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  trackNeighbourhoodFeedback,
  trackNeighbourhoodShareFeedback,
} from './neighbourhood.tracking';

const { t, locale } = useI18n();

const selectedFeedback = ref<'positive' | 'negative' | null>(null);
const readLocaleMap = (rawValue: string): Record<string, string> => {
  try {
    return JSON.parse(rawValue) as Record<string, string>;
  } catch (error) {
    void error;
    return {};
  }
};
const positiveFeedbackUrls = readLocaleMap(
  import.meta.env.VUE_NEIGHBOURHOOD_FEEDBACK_POSITIVE_URLS ?? '{}',
);
const negativeFeedbackUrls = readLocaleMap(
  import.meta.env.VUE_NEIGHBOURHOOD_FEEDBACK_NEGATIVE_URLS ?? '{}',
);
const activeLocale = computed(() => (locale.value || 'en').split('-')[0].toLowerCase());
const feedbackUrl = computed(() => {
  const localeKey = activeLocale.value;
  const positiveUrl = positiveFeedbackUrls[localeKey] ?? positiveFeedbackUrls.en ?? '';
  const negativeUrl = negativeFeedbackUrls[localeKey] ?? negativeFeedbackUrls.en ?? '';
  return selectedFeedback.value === 'negative' ? negativeUrl : positiveUrl;
});
const shareLinkHref = computed(() => {
  if (!selectedFeedback.value) {
    return '';
  }
  return feedbackUrl.value;
});
const isShareEnabled = computed(() => Boolean(shareLinkHref.value));
const showShareLink = computed(() => Boolean(selectedFeedback.value));

const handleFeedback = (type: 'positive' | 'negative') => {
  if (selectedFeedback.value === type) {
    selectedFeedback.value = null;
    return;
  }

  selectedFeedback.value = type;

  trackNeighbourhoodFeedback(type);
};

const handleShareClick = (event: MouseEvent) => {
  if (!isShareEnabled.value) {
    event.preventDefault();
    return;
  }

  trackNeighbourhoodShareFeedback();
};
</script>

<style module lang="scss">
.feedbackSection {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px 0 0 0;
  border-top: 1px solid #e5e7eb;
  margin-top: 24px;
  flex-wrap: wrap;
  justify-content: flex-start;
}

.feedbackGroup {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.text {
  font-size: 1rem;
  color: #666666;
  line-height: 24px;
}

.button {
  background: #e8e8e8;
  border: none;
  border-radius: 999px;
  font-size: 14px;
  cursor: pointer;
  padding: 0 12px;
  height: 36px;
  transition: transform 0.2s ease;
  opacity: 1;
  color: #666666;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &:hover:not(:disabled) {
    transform: scale(1.15);
    opacity: 0.8;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.3;
  }

  &.selected {
    transform: scale(1.2);
    opacity: 1;
  }
}

.positiveButton {
  background: #e8e8e8;
}

.negativeButton {
  background: #e8e8e8;
}

.buttonIcon {
  width: 24px;
  height: 24px;
  color: #666666;
}

.buttonText {
  font-size: 14px;
  line-height: 21px;
}

.shareLink {
  margin-left: 8px;
  font-size: 0.875rem;
  color: #2266e8;
  text-decoration: none;
  line-height: 21px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.shareLinkDisabled {
  opacity: 0.5;
  pointer-events: none;
}

.shareIcon {
  width: 24px;
  height: 24px;
}

.shareLink:hover {
  text-decoration: underline;
}
</style>
